import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/apiUtils';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Checkbox
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import format from 'date-fns/format';

const MoegoScheduler = () => {
  const [config, setConfig] = useState({
    notification_email: '',
    start_time: null,
    end_time: null,
    frequency_minutes: null,  // No default, will be set from server
    is_active: false
  });

  const [status, setStatus] = useState({
    is_running: false,
    start_time: null,
    records_processed: 0,
    current_status: null,
    error_message: null
  });

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Manual sync state
  const [manualSyncIsInitial, setManualSyncIsInitial] = useState(false);
  const [manualSyncLoading, setManualSyncLoading] = useState(false);
  const [manualSyncError, setManualSyncError] = useState(null);
  const [manualSyncSuccess, setManualSyncSuccess] = useState(false);
  const [manualSyncResults, setManualSyncResults] = useState(null);

  // Fetch configuration once on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setError(null);
        console.log('Fetching scheduler configuration...');
        // Using apiFetch which automatically handles authorization headers and URL resolution
        const response = await apiFetch('/api/scheduler/config');

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch scheduler configuration');
        }

        const data = await response.json();
        console.log('Received scheduler configuration:', data);
        
        // Parse and validate frequency value from server
        let frequency = null;
        if (data?.frequency_minutes !== undefined && data?.frequency_minutes !== null) {
          frequency = parseInt(data.frequency_minutes);
          // Validate frequency value strictly
          if (isNaN(frequency) || frequency <= 0 || frequency > 1440) {
            console.error('Invalid frequency value from server:', {
              raw: data.frequency_minutes,
              parsed: frequency,
              valid_range: '1-1440 minutes',
              error: 'Value must be between 1 and 1440'
            });
            throw new Error('Invalid frequency value received from server (must be between 1 and 1440 minutes)');
          }
          console.log('Validated frequency value:', frequency);
        } else {
          console.log('No frequency value received from server, using null');
        }
        
        // Log the full configuration we're about to set
        console.log('Setting configuration:', {
          notification_email: data?.notification_email || '',
          start_time: data?.start_time ? `Parsed from: ${data.start_time}` : 'null',
          end_time: data?.end_time ? `Parsed from: ${data.end_time}` : 'null',
          frequency_minutes: frequency,
          is_active: data?.is_active || false
        });
        setConfig({
          notification_email: data?.notification_email || '',
          start_time: data?.start_time ? new Date(`2000-01-01T${data.start_time}`) : null,
          end_time: data?.end_time ? new Date(`2000-01-01T${data.end_time}`) : null,
          frequency_minutes: frequency,
          is_active: data?.is_active || false
        });
      } catch (err) {
        console.error('Error fetching configuration:', err);
        setError(`Failed to load scheduler configuration: ${err.message}`);
        // Set default config on error
        setConfig({
          notification_email: '',
          start_time: null,
          end_time: null,
          frequency_minutes: null,
          is_active: false
        });
      } finally {
        // Always set loading to false after config fetch, regardless of success
        setLoading(false);
      }
    };

    fetchConfig();
  }, []); // Only run once on mount

  // Function to fetch status and logs
  const fetchLogsAndStatus = async () => {
    try {
      console.log('Fetching scheduler status and logs...');
      setError(null);

      // Get scheduler status
      // Using apiFetch which automatically handles authorization headers and URL resolution
      const statusResponse = await apiFetch('/api/scheduler/status');

      if (!statusResponse.ok) {
        throw new Error('Failed to fetch scheduler status');
      }

      const statusData = await statusResponse.json();
      console.log('Received scheduler status:', statusData);

      // Update status state
      setStatus(statusData.current_state || {
        is_running: false,
        start_time: null,
        records_processed: 0,
        current_status: null,
        error_message: null
      });

      // Fetch logs
      // Using apiFetch which automatically handles authorization headers and URL resolution
      const logsResponse = await apiFetch('/api/scheduler/logs');

      if (!logsResponse.ok) {
        throw new Error('Failed to fetch scheduler logs');
      }

      const logsData = await logsResponse.json();
      console.log('Received scheduler logs:', logsData);
      
      setLogs(logsData);
    } catch (err) {
      console.error('Error fetching status/logs:', err);
      setError(err.message);
    }
  };

  // Fetch status and logs periodically
  useEffect(() => {
    let isMounted = true; // For cleanup
    
    const fetchData = async () => {
      if (isMounted) {
        await fetchLogsAndStatus();
      }
    };

    fetchData();
    
    // Set up periodic refresh for status and logs only
    const intervalId = setInterval(fetchData, 30000); // Refresh every 30 seconds
    
    return () => {
      isMounted = false; // Cleanup flag
      clearInterval(intervalId);
    };
  }, []); // Only run once to set up the interval

  const handleSave = async () => {
    try {
      setSaveError(null);
      setSaveSuccess(false);

      // Validate all required fields
      if (!config.notification_email || !config.start_time || !config.end_time || !config.frequency_minutes) {
        throw new Error('All fields are required, including frequency');
      }

      // Validate frequency value
      const frequency = parseInt(config.frequency_minutes);
      if (isNaN(frequency) || frequency <= 0 || frequency > 1440) {
        throw new Error('Frequency must be between 1 and 1440 minutes (24 hours)');
      }

      console.log('Saving configuration:', {
        ...config,
        frequency_minutes: frequency,
        start_time: format(config.start_time, 'HH:mm'),
        end_time: format(config.end_time, 'HH:mm')
      });

      // Format times for API
      const formattedConfig = {
        ...config,
        start_time: format(config.start_time, 'HH:mm'),
        end_time: format(config.end_time, 'HH:mm')
      };

      // Using apiFetch which automatically handles authorization headers and URL resolution
      const response = await apiFetch('/api/scheduler/config', {
        method: 'PUT',
        body: JSON.stringify(formattedConfig)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save configuration');
      }

      const savedConfig = await response.json();
      console.log('Configuration saved successfully:', savedConfig);

      setSaveSuccess(true);
    } catch (err) {
      console.error('Error saving configuration:', err);
      setSaveError(`Failed to save configuration: ${err.message}`);
    }
  };

  const handleStartStop = async () => {
    try {
      setSaveError(null);
      const endpoint = config.is_active ? '/api/scheduler/stop' : '/api/scheduler/start';
      
      // Using apiFetch which automatically handles authorization headers and URL resolution
      const response = await apiFetch(endpoint, {
        method: 'POST'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to ' + (config.is_active ? 'stop' : 'start') + ' scheduler');
      }

      // Update local state
      setConfig(prev => ({ ...prev, is_active: !prev.is_active }));
    } catch (err) {
      setSaveError(err.message);
    }
  };
  
  // Handler for manual sync
  const handleManualSync = async () => {
    const endpoint = manualSyncIsInitial ? '/api/integrations/moego/sync?initial=true' : '/api/integrations/moego/sync';
    
    console.log('Starting manual MoeGo sync...', {
      isInitialLoad: manualSyncIsInitial,
      endpoint
    });

    setManualSyncLoading(true);
    setManualSyncError(null);
    setManualSyncSuccess(false);
    setManualSyncResults(null);

    try {
      // Use the user's token for authorization - apiFetch will use the token from localStorage automatically
      // Using apiFetch which handles URL resolution for cross-device compatibility
      const response = await apiFetch(endpoint, {
        method: 'POST'
      });

      console.log('Manual sync response:', {
        status: response.status,
        statusText: response.statusText
      });

      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || response.statusText;
        } catch (e) {
          errorMessage = response.statusText;
        }

        if (response.status === 401) {
          throw new Error('Please log in to use this feature');
        } else if (response.status === 403) {
          throw new Error('You do not have permission to use this feature');
        } else if (response.status === 503) {
          throw new Error('MoeGo API service is not properly configured');
        } else {
          throw new Error(`Sync failed: ${errorMessage}`);
        }
      }

      const data = await response.json();
      setManualSyncSuccess(true);
      setManualSyncResults(data);
      
      // Refresh the logs to show the new sync entry
      fetchLogsAndStatus();
    } catch (err) {
      console.error('Error during manual sync:', err);
      setManualSyncError(err.message || 'Failed to sync with MoeGo API');
    } finally {
      setManualSyncLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          MoeGo Sync Scheduler
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Configuration saved successfully
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Notification Email"
            value={config.notification_email}
            onChange={(e) => setConfig(prev => ({ ...prev, notification_email: e.target.value }))}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TimePicker
              label="Start Time"
              value={config.start_time}
              onChange={(newValue) => setConfig(prev => ({ ...prev, start_time: newValue }))}
              sx={{ flex: 1 }}
              slotProps={{ textField: { fullWidth: true } }}
              views={['hours', 'minutes']}
              format="HH:mm"
            />
            <TimePicker
              label="End Time"
              value={config.end_time}
              onChange={(newValue) => setConfig(prev => ({ ...prev, end_time: newValue }))}
              sx={{ flex: 1 }}
              slotProps={{ textField: { fullWidth: true } }}
              views={['hours', 'minutes']}
              format="HH:mm"
            />
          </Box>

          <TextField
            fullWidth
            type="number"
            label="Frequency (minutes)"
            value={config.frequency_minutes || ''}
            onChange={(e) => {
              const rawValue = e.target.value;
              // Only update if empty or valid number
              if (rawValue === '' || !isNaN(parseInt(rawValue))) {
                const value = rawValue === '' ? null : parseInt(rawValue);
                setConfig(prev => ({ ...prev, frequency_minutes: value }));
              }
            }}
            error={
              config.frequency_minutes !== null && 
              (isNaN(config.frequency_minutes) || 
               config.frequency_minutes <= 0 || 
               config.frequency_minutes > 1440)
            }
            helperText={
              config.frequency_minutes !== null && 
              (isNaN(config.frequency_minutes) || 
               config.frequency_minutes <= 0 || 
               config.frequency_minutes > 1440)
                ? "Invalid frequency. Must be between 1 and 1440 minutes (24 hours)"
                : "Enter how often to run the sync (in minutes). Valid range: 1-1440"
            }
            inputProps={{
              min: 1,
              max: 1440,
              step: 1,
              onKeyPress: (e) => {
                // Allow only numbers and control keys
                if (!/[\d\b]/.test(e.key)) {
                  e.preventDefault();
                }
              }
            }}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.is_active}
                  onChange={handleStartStop}
                  color="primary"
                />
              }
              label={config.is_active ? 'Scheduler Active' : 'Scheduler Inactive'}
            />
            <Button
              variant="contained"
              onClick={handleSave}
              color="primary"
            >
              Save Configuration
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Typography variant="h6" gutterBottom>
          Current Status
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography>
            Status: {status.is_running ? (
              <Box component="span" sx={{ color: 'success.main' }}>
                Running
              </Box>
            ) : (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                Idle
              </Box>
            )}
          </Typography>
          
          {status.is_running && (
            <>
              <Typography>
                Started: {status.start_time ? (() => {
                  // Apply the same date normalization as in the table
                  try {
                    // SQLite 'DATETIME('now')' format typically doesn't include timezone info
                    if (typeof status.start_time === 'string' && 
                        status.start_time.includes('-') && 
                        status.start_time.includes(':') && 
                        !status.start_time.includes('Z') && 
                        !status.start_time.includes('+')) {
                      // Add 'Z' to ensure it's treated as UTC
                      const date = new Date(status.start_time + 'Z');
                      return date.toLocaleString();
                    } else {
                      const date = new Date(status.start_time);
                      return date.toLocaleString();
                    }
                  } catch (e) {
                    console.error(`Error parsing status start_time: ${e.message}`);
                    return 'Date Error';
                  }
                })() : 'N/A'}
              </Typography>
              <Typography>
                Records Processed: {status.records_processed}
              </Typography>
            </>
          )}
          
          {status.error_message && (
            <Typography color="error">
              Last Error: {status.error_message}
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Typography variant="h6" gutterBottom>
          
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Typography variant="h6" gutterBottom>
          Manual Sync
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Need to run a sync now? Use this section to manually trigger a data sync with MoeGo.
            Choose between an initial load (all historical data) or an incremental sync (recent changes only).
          </Typography>

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(e) => setManualSyncIsInitial(e.target.checked)}
                  checked={manualSyncIsInitial}
                  disabled={manualSyncLoading}
                />
              }
              label="Initial Load (fetches all historical data)"
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleManualSync}
              disabled={manualSyncLoading}
              sx={{ mr: 2 }}
            >
              {manualSyncLoading ? 'Running MoeGo Sync...' : 'Run MoeGo Sync'}
            </Button>
            {manualSyncLoading && <CircularProgress size={24} />}
          </Box>

          {manualSyncError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {manualSyncError}
            </Alert>
          )}

          {manualSyncSuccess && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                MoeGo sync completed successfully
              </Alert>
              {manualSyncResults && (
                <Box sx={{ pl: 2, mb: 2 }}>
                  <Typography>
                    • Appointments processed: {manualSyncResults.appointments || 0}
                  </Typography>
                  <Typography>
                    • Customers processed: {manualSyncResults.customers || 0}
                  </Typography>
                  <Typography>
                    • Staff processed: {manualSyncResults.staff || 0}
                  </Typography>
                  {manualSyncResults.errors && manualSyncResults.errors.length > 0 && (
                    <Typography color="warning.main" sx={{ mt: 1 }}>
                      Warnings: {manualSyncResults.errors.length} issue(s) encountered
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Typography variant="h6" gutterBottom>
          Recent Runs
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Start Time</TableCell>
                <TableCell>Completion Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Records Processed</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => {
                // Helper function to normalize date handling and ensure consistent timezone display
                const normalizeDate = (timestamp) => {
                  if (!timestamp) return 'N/A';
                  
                  try {
                    // SQLite 'DATETIME('now')' format typically doesn't include timezone info
                    // Force interpretation as UTC if it appears to be in SQLite format
                    let date;
                    if (typeof timestamp === 'string' && timestamp.includes('-') && 
                        timestamp.includes(':') && !timestamp.includes('Z') && 
                        !timestamp.includes('+')) {
                      // Add 'Z' to ensure it's treated as UTC
                      date = new Date(timestamp + 'Z');
                    } else {
                      // Regular date parsing
                      date = new Date(timestamp);
                    }
                    
                    if (isNaN(date.getTime())) {
                      console.error(`Invalid date from timestamp: ${timestamp}`);
                      return 'Invalid Date';
                    }
                    
                    return date.toLocaleString();
                  } catch (e) {
                    console.error(`Error parsing date: ${e.message}, timestamp: ${timestamp}`);
                    return 'Date Error';
                  }
                };
                
                return (
                  <TableRow key={log.id}>
                    <TableCell>{normalizeDate(log.run_timestamp)}</TableCell>
                    <TableCell>{normalizeDate(log.completion_timestamp)}</TableCell>
                    <TableCell>
                      <Box component="span" sx={{ 
                        color: log.status === 'success' ? 'success.main' : 'error.main'
                      }}>
                        {log.status}
                      </Box>
                    </TableCell>
                    <TableCell>{log.records_processed || 0}</TableCell>
                    <TableCell>{log.error_message || '-'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default MoegoScheduler;